import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Column } from "primereact/column";
import styled from "@emotion/styled";
import { Wrapper } from "../../../components/core/Table/GridStyles";
import { MediumText, SmallText, FlexRow, SLNotification } from "../../../components/core/Utility";
import moment from "moment";
import { css } from "@emotion/react";
import { InputText } from "primereact/inputtext";
import _ from "lodash";
import { generate } from "shortid";
import { EDIT_FIELD_TYPES } from "../../../constants/ApplicationConstants";
import { HARDWARE_REQUEST_STATUS } from "./EditVehicleConstants";
import { Tooltip } from "primereact/tooltip";

const EllipsisColumn = css`
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const ColumnHeaderWrapper = styled.div`
  display: inline-block;
  width: 100%;
`;
const CustomNotification = styled(SLNotification)`
  & div {
    justify-content: flex-start;
    max-width: 100%;
    & span:first-child {
      flex: 2 1 0%;
    }
  }
`;

const FullWidthWrapper = styled(Wrapper)`
  max-width: 100% !important;
  width: 100% !important;
  .p-datatable .p-datatable-thead > tr > th {
    padding: 0px 4px !important;
  }
  .p-datatable .p-datatable-tbody > tr > td {
    padding: 0px 4px !important;
    font-size: 11px !important;
  }
`;

const formattedData = (data) => {
  return data == null ? "--" : data;
};

const formatDate = (value, dateFormat, timezone = "") => {
  return dateFormat
    ? value
      ? `${moment(value).format(dateFormat)} ${timezone}`.trim()
      : formattedData(value)
    : value.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      });
};

const CaseNumberFormatter = styled(SmallText)`
  & span {
    font-weight: ${(props) => (props.isBold ? "700" : "")};
  }
`;

export const HardwareApprovalListGridComponent = ({
  title,
  /** method for hyperlink click */
  hyperLinkClicked,
  /** message in case of no records*/
  noDataMessage = "No data to display",
  rows = [],
  columns = [],
  flyOutMenu,
  border,
  isHyperLink,
  renderDataWithImage,
  caseIdentifier,
  whiteSpace,
  dropDownValues,
  initialRows = rows,
  fieldValueMappings,
  additionalGlobalSearchFields = [],
  selectionAutoFocus = true,
  onDropDownClick,
  headerNotification,
  notification,
}) => {
  const [first, setFirst] = useState(0);
  const [rowsInAPage, setRowsInAPage] = useState(5);
  const dt = useRef(null);
  const [filters, setFilters] = useState(null);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [modifiedRows, setModifiedRows] = useState([]);

  useEffect(() => {
    setUpdatedRows(rows);
  }, [rows]);

  useEffect(() => {
    determineSaveEnabled();
  }, [updatedRows, initialRows]);

  const determineSaveEnabled = () => {
    let updatedItems = _.differenceWith(updatedRows, initialRows, _.isEqual);
    updatedItems.length == 0 ? setSaveDisabled(true) : setSaveDisabled(false);
    setModifiedRows(updatedItems);
  };

  const updateRowData = (e, rowData, field) => {
    rowData[field] = e.target.value;
    const col = columns.find((col) => col.value === field);
    let dependentFields = col && col.dependentFields;
    let newRows = [...updatedRows];
    let rowToBeUpdated = newRows.find((row) => row === rowData);
    let mappingList = fieldValueMappings && fieldValueMappings[field];
    if (dependentFields && mappingList) {
      //update values for dependent fields
      dependentFields.forEach((dependentField) => {
        const mapping = mappingList.find((mappingData) => mappingData[field] === e.target.value);
        if (mapping) {
          rowToBeUpdated[dependentField] = mapping[dependentField];
        }
      });
    }
    rowToBeUpdated[field] = e.target.value;
    setUpdatedRows(newRows);
  };

  const getEditTemplate = (rowData, colAttributes, data) => {
    // Custom handling for assignee dropdown
    if (colAttributes.value === "assignee") {
      return (
        <Dropdown
          value={rowData.selectedAssignee || ""}
          options={dropDownValues.assignee}
          onChange={(e) => {
            updateRowData({ target: { value: e.value } }, rowData, "selectedAssignee");
            if (typeof onDropDownClick === "function") {
              // Find the row index in updatedRows
              const rowIndex = updatedRows.findIndex((r) => r === rowData);
              onDropDownClick(rowIndex, e.value);
            }
          }}
          placeholder="Select Assignee"
          style={{ width: 140 }}
          optionLabel="label"
          optionValue="value"
        />
      );
    }
    switch (colAttributes.editFieldType) {
      case EDIT_FIELD_TYPES.dropDown:
        return (
          <Dropdown
            value={rowData[data.field]}
            options={dropDownValues[data.field]}
            onChange={(e) => updateRowData(e, rowData, data.field)}
            placeholder="Select"
            style={{ minWidth: 140 }}
            optionLabel="label"
            optionValue="value"
          />
        );
      default:
        return <InputText style={colAttributes.editFieldStyle} value={rowData[data.field]} onChange={(e) => updateRowData(e, rowData, data.field)} />;
    }
  };

  const getBodyToDisplay = (rowData, data) => {
    let colAttributes = columns.find((col) => col.value == data.field);
    if (data.field === "bundle") {
      // Get unique subscription names for the tooltip
      let uniqueNames = [];
      if (typeof rowData.currentHardwareMapping === "object" && Array.isArray(rowData.currentHardwareMapping)) {
        uniqueNames = rowData.currentHardwareMapping.map((item) => item && item.subscriptionName).filter((name) => !!name);
        uniqueNames = Array.from(new Set(uniqueNames));
      }
      // Format tooltip content with each name on a new line
      const tooltipContent = uniqueNames.length ? uniqueNames.join("\n") : "No subscriptions";
      return (
        <>
          <SmallText primitive="span" id={`cell-bundle-${rowData.id || rowData.key || Math.random()}`}>
            {formattedData(rowData[data.field])}
          </SmallText>
          <Tooltip target={`#cell-bundle-${rowData.id || rowData.key || Math.random()}`} content={tooltipContent} />
        </>
      );
    }
    if (data.field === "status") {
      return (
        <>
          <SmallText primitive="span" id={`cell-bundle-${rowData.id || rowData.key || Math.random()}`}>
            {rowData[data.field] ? HARDWARE_REQUEST_STATUS[rowData[data.field]] : "--"}
          </SmallText>
        </>
      );
    }
    if (colAttributes.isEditable) {
      return getEditTemplate(rowData, colAttributes, data);
    }
    if (colAttributes.checkSouce) {
      return (
        <CaseNumberFormatter primitive="span" isBold={caseIdentifier(rowData[data.field])}>
          {rowData[data.field]}
        </CaseNumberFormatter>
      );
    } else {
      if (colAttributes.isHyperLink) {
        if (colAttributes.isList) {
          return <SmallText primitive="span">{getListToDisplay()}</SmallText>;
        } else {
          if (isHyperLink && !isHyperLink(rowData)) {
            return <SmallText primitive="span">{formattedData(rowData[data.field])}</SmallText>;
          }
          return (
            <SmallText
              primitive="span"
              style={{ "text-decoration": "underline", cursor: "pointer" }}
              onClick={() => hyperLinkClicked(rowData, colAttributes.key)}
            >
              {renderData(rowData[data.field], colAttributes)}
            </SmallText>
          );
        }
      } else if (colAttributes.ellipsis) {
        return (
          <SmallText primitive="span" css={EllipsisColumn}>
            {renderData(rowData[data.field], colAttributes)}
          </SmallText>
        );
      } else {
        return renderContentForNonHyperlink(rowData, colAttributes, data);
      }
    }
  };

  function getListToDisplay(rowData, colAttributes, data) {
    return rowData[data.field].map((itemInList, index) => {
      return (
        <>
          <SmallText
            primitive="span"
            key={index}
            style={{ "text-decoration": "underline", cursor: "pointer" }}
            onClick={() => hyperLinkClicked(rowData, colAttributes.key)}
          >
            {formattedData(itemInList[colAttributes.itemToDisplayInList])}
          </SmallText>
          <br />
        </>
      );
    });
  }

  const renderData = (data, colAttributes, rowData) => {
    if (colAttributes.constantValue) {
      return formattedData(colAttributes.constantValue);
    }
    return colAttributes.containsImage ? renderDataWithImage(formattedData(data), rowData) : formattedData(data);
  };

  const renderContentForNonHyperlink = (rowData, colAttributes, data) => {
    if (colAttributes.isList) {
      return <SmallText primitive="span">{getListToDisplay(rowData, colAttributes, data)}</SmallText>;
    } else if (colAttributes.isDate) {
      return <SmallText primitive="span">{formatDate(rowData[data.field], colAttributes.dateFormat, rowData.timezone)}</SmallText>;
    } else {
      return <SmallText primitive="span">{renderData(rowData[data.field], colAttributes, rowData)}</SmallText>;
    }
  };

  const renderHeader = () => {
    return (
      <>
        {notification && (
          <div css={{ marginTop: "1rem" }}>
            <CustomNotification key={generate()} type={notification.type} title={notification.title} fullBleed={true} />
          </div>
        )}
        <FlexRow justifyContent="space-between" css={{ marginBottom: "0.5rem" }}>
          {title && (
            <FlexRow alignItems="flex-end">
              <MediumText bold>{title}</MediumText>
            </FlexRow>
          )}
        </FlexRow>
        {headerNotification && (
          <div css={{ margin: "1.5rem 0rem 0.625rem 0rem" }}>
            <CustomNotification key={generate()} type={headerNotification?.type} title={headerNotification?.title} fullBleed={true} />
          </div>
        )}
      </>
    );
  };

  const renderEmptyMessage = () => {
    return <SmallText>{noDataMessage}</SmallText>;
  };

  const ColumnHeader = ({ col, ariaLabel, tooltip }) => {
    return (
      <ColumnHeaderWrapper>
        <SmallText bold aria-label={ariaLabel} id={`header-${col}`}>
          {col}
        </SmallText>
        {tooltip && <Tooltip target={`#header-${col}`} content={tooltip} />}
      </ColumnHeaderWrapper>
    );
  };

  const cellFontStyle = {
    color: "#000000",
    fontFamily: "Verizon NHG eTX, Helvetica, Arial, sans-serif",
    fontWeight: "700",
    fontSize: "12px",
    lineHeight: "16px",
    letterSpacing: "0px",
    textAlign: "left",
    textTransform: "none",
  };

  return (
    <div style={{ width: "100%" }}>
      <FullWidthWrapper
        colSizes={{ mobile: 4, tablet: 12, desktop: 12 }}
        autoResizeHead={true}
        fontSize="14px"
        colGutter={true}
        whiteSpace={whiteSpace}
        flyOutMenu={flyOutMenu}
        border={border}
      >
        <DataTable
          header={renderHeader()}
          ref={dt}
          emptyMessage={renderEmptyMessage()}
          filters={filters}
          responsiveLayout="scroll"
          value={updatedRows}
          first={first}
          rows={rowsInAPage}
          stripedRows
          totalRecords={Array.isArray(rows) ? rows.length : 0}
          globalFilterFields={[...columns.map((column) => column.value), ...additionalGlobalSearchFields]}
          selectionAutoFocus={selectionAutoFocus}
          tabIndex={0}
          style={cellFontStyle}
        >
          {
            //added it as part of VZC42148-106
            columns.map((item) => {
              const isBundle = item.value === "bundle";
              return item.isCustomFilter ? (
                <Column
                  key={item.key}
                  field={item.value}
                  columnKey={item.key}
                  body={getBodyToDisplay}
                  header={<ColumnHeader col={item.key} tooltip={isBundle ? "Bundle details" : undefined} />}
                  style={{
                    minWidth: item.width || "200px",
                    width: item.width || "200px",
                    minHeight: "34px",
                    padding: "4px 0 4px 0",
                    backgroundColor: flyOutMenu ? "white" : undefined,
                    ...item.style,
                  }}
                  headerStyle={cellFontStyle}
                  bodyStyle={cellFontStyle}
                  sortable={item.isSortable}
                  filter={item.isFilterEnabled}
                  frozen={item.isFrozen}
                  showFilterMatchModes={false}
                  filterMenuClassName="custom-filter"
                  filterClear={<></>}
                  filterApply={<></>}
                />
              ) : (
                <Column
                  key={item.key}
                  field={item.value}
                  columnKey={item.key}
                  body={getBodyToDisplay}
                  header={<ColumnHeader col={item.key} ariaLabel={item.ariaLabel} tooltip={isBundle ? "Bundle details" : undefined} />}
                  style={{
                    minWidth: item.width || "200px",
                    width: item.width || "200px",
                    minHeight: "34px",
                    padding: "4px 0 4px 0",
                    backgroundColor: flyOutMenu ? "white" : undefined,
                    ...item.style,
                  }}
                  headerStyle={cellFontStyle}
                  bodyStyle={cellFontStyle}
                  sortable={item.isSortable}
                  filter={item.isFilterEnabled}
                  frozen={item.isFrozen}
                />
              );
            })
          }
        </DataTable>
      </FullWidthWrapper>
    </div>
  );
};
